import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import type { PaymentRequest } from "../types/payment";
import { ACTIONABLE_STATUSES } from "../types/common";
import PaymentStatusBadge from "./PaymentStatusBadge";
import PaymentTypeBadge from "./PaymentTypeBadge";
import { formatAmount, formatDate } from "../utils/formatters";

const thStyle = {
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "16px",
  color: "text.secondary",
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
};

const tdStyle = {
  fontSize: "14px",
  py: "12px",
};

interface ColumnDef {
  key: string;
  header: string;
  align?: "left" | "right" | "center";
  render: (r: PaymentRequest) => React.ReactNode;
}

const COLUMNS: ColumnDef[] = [
  {
    key: "reference",
    header: "Reference",
    render: (r) => (
      <Box>
        <Typography fontSize="14px" fontWeight={500} sx={{ whiteSpace: "nowrap" }}>
          {r.owner_reference_name ?? r.owner_reference}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {r.owner_type}
        </Typography>
      </Box>
    ),
  },
  {
    key: "vendor",
    header: "Vendor",
    render: (r) => (
      <Typography fontSize="14px" sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {r.vendor_facility_name ?? "—"}
      </Typography>
    ),
  },
  {
    key: "payment_type",
    header: "Type",
    render: (r) => <PaymentTypeBadge type={r.payment_type} />,
  },
  {
    key: "requested_amount",
    header: "Requested",
    align: "right",
    render: (r) => formatAmount(r.requested_amount),
  },
  {
    key: "remaining_amount",
    header: "Remaining",
    align: "right",
    render: (r) => formatAmount(r.remaining_amount),
  },
  {
    key: "created_at",
    header: "Created Date",
    render: (r) => (
      <Typography fontSize="14px" sx={{ whiteSpace: "nowrap" }}>
        {formatDate(r.created_at)}
      </Typography>
    ),
  },
  {
    key: "expected_payment_date",
    header: "Expected Date",
    render: (r) =>
      r.expected_payment_date ? formatDate(r.expected_payment_date) : "—",
  },
  {
    key: "overdue",
    header: "Overdue",
    align: "center",
    render: (r) => (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "4px",
          bgcolor: r.overdue_flag ? "#FFEBEE" : "#E8F5E9",
          border: "1px solid",
          borderColor: r.overdue_flag ? "#C62828" : "#2E7D32",
          color: r.overdue_flag ? "#C62828" : "#2E7D32",
          fontSize: "13px",
          fontWeight: 700,
        }}
      >
        {r.overdue_flag ? "Y" : "N"}
      </Box>
    ),
  },
  {
    key: "days_overdue",
    header: "Days OD",
    align: "center",
    render: (r) => (
      <Typography
        fontSize="14px"
        fontWeight={r.days_overdue > 0 ? 600 : 400}
        sx={{ color: r.days_overdue > 0 ? "#C62828" : "text.secondary" }}
      >
        {r.days_overdue}
      </Typography>
    ),
  },
  {
    key: "msme",
    header: "MSME",
    align: "center",
    render: (r) =>
      r.msme_deadline_date ? (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            px: "6px",
            height: 22,
            borderRadius: "4px",
            bgcolor: "#FFEBEE",
            border: "1px solid #C62828",
            color: "#C62828",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.4px",
          }}
        >
          MSME
        </Box>
      ) : (
        <Typography fontSize="14px" color="text.disabled">—</Typography>
      ),
  },
  {
    key: "msme_deadline_date",
    header: "MSME Deadline",
    render: (r) =>
      r.msme_deadline_date ? (
        <Typography
          fontSize="13px"
          fontWeight={r.msme_breach_risk_flag ? 700 : 400}
          sx={{ color: r.msme_breach_risk_flag ? "#C62828" : "text.primary", whiteSpace: "nowrap" }}
        >
          {formatDate(r.msme_deadline_date)}
        </Typography>
      ) : (
        <Typography fontSize="14px" color="text.disabled">—</Typography>
      ),
  },
  {
    key: "request_status",
    header: "Status",
    render: (r) => <PaymentStatusBadge status={r.request_status} />,
  },
];

interface PaymentTableProps {
  requests: PaymentRequest[];
  isLoading: boolean;
  onRowClick: (request: PaymentRequest) => void;
  onApprove?: (request: PaymentRequest) => void;
  onReject?: (request: PaymentRequest) => void;
  selectedIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
  actionPending?: boolean;
}

export default function PaymentTable({
  requests,
  isLoading,
  onRowClick,
  onApprove,
  onReject,
  selectedIds,
  onSelectionChange,
  actionPending,
}: PaymentTableProps) {
  const showCheckboxes = selectedIds !== undefined && onSelectionChange !== undefined;
  const actionableRequests = requests.filter((r) => ACTIONABLE_STATUSES.has(r.request_status));
  const allActionableSelected =
    actionableRequests.length > 0 && actionableRequests.every((r) => selectedIds?.has(r.id));
  const someActionableSelected = actionableRequests.some((r) => selectedIds?.has(r.id));

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange || !selectedIds) return;
    const newSet = new Set(selectedIds);
    if (e.target.checked) {
      actionableRequests.forEach((r) => newSet.add(r.id));
    } else {
      actionableRequests.forEach((r) => newSet.delete(r.id));
    }
    onSelectionChange(newSet);
  };

  const handleRowCheckbox = (request: PaymentRequest, checked: boolean) => {
    if (!onSelectionChange || !selectedIds) return;
    const newSet = new Set(selectedIds);
    if (checked) newSet.add(request.id);
    else newSet.delete(request.id);
    onSelectionChange(newSet);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const showSelectAll = showCheckboxes && requests.length > 0;
  const hasActionable = actionableRequests.length > 0;
  const colSpan = COLUMNS.length + 1 + (showSelectAll ? 1 : 0);

  return (
    <TableContainer sx={{ overflow: "visible" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: "white", "& th": { borderBottomWidth: 2 } }}>
            {showSelectAll && (
              <TableCell padding="checkbox">
                {hasActionable && (
                  <Checkbox
                    size="small"
                    indeterminate={someActionableSelected && !allActionableSelected}
                    checked={allActionableSelected}
                    onChange={handleSelectAll}
                    inputProps={{ "aria-label": "Select all actionable payments" }}
                  />
                )}
              </TableCell>
            )}
            {COLUMNS.map((col) => (
              <TableCell key={col.key} sx={thStyle} align={col.align ?? "left"}>
                {col.header}
              </TableCell>
            ))}

            <TableCell sx={thStyle}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.length === 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} align="center">
                <Typography color="text.secondary" py={4}>
                  No payment requests found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {requests.map((request) => (
            <TableRow
              key={request.id}
              hover
              selected={selectedIds?.has(request.id) ?? false}
              onClick={() => onRowClick(request)}
              sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
            >
              {showCheckboxes && (
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  {ACTIONABLE_STATUSES.has(request.request_status) && (
                    <Checkbox
                      size="small"
                      checked={selectedIds?.has(request.id) ?? false}
                      onChange={(e) => handleRowCheckbox(request, e.target.checked)}
                      inputProps={{ "aria-label": `Select payment ${request.id}` }}
                    />
                  )}
                </TableCell>
              )}
              {COLUMNS.map((col) => (
                <TableCell key={col.key} sx={tdStyle} align={col.align ?? "left"}>
                  {col.render(request)}
                </TableCell>
              ))}
              <TableCell sx={tdStyle} onClick={(e) => e.stopPropagation()}>
                {ACTIONABLE_STATUSES.has(request.request_status) && (
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      disabled={actionPending}
                      onClick={() => onApprove?.(request)}
                      sx={{ color: "#2E7D32" }}
                      aria-label="Approve payment"
                    >
                      <Check fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={actionPending}
                      onClick={() => onReject?.(request)}
                      sx={{ color: "#C62828" }}
                      aria-label="Reject payment"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
